import type {
  AuthenticationResult,
  EventMessage,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '@/config/authConfig';

const ACCOUNT_SUCCESS_EVENTS = new Set<string>([
  EventType.LOGIN_SUCCESS,
  EventType.ACQUIRE_TOKEN_SUCCESS,
  EventType.SSO_SILENT_SUCCESS,
]);

/**
 * MSAL never chooses an active account by itself. Following the msal-browser
 * guidance, mark the account of every successful sign-in or token response as
 * active, so redirect returns, popups and onboarding flows all leave MSAL
 * pointing at the account the app is actually using.
 */
export function syncActiveAccountFromEvent(
  instance: Pick<
    IPublicClientApplication,
    'getActiveAccount' | 'setActiveAccount'
  >,
  event: EventMessage
): void {
  if (!ACCOUNT_SUCCESS_EVENTS.has(event.eventType)) return;

  const account = (event.payload as AuthenticationResult | null)?.account;
  if (!account) return;

  if (instance.getActiveAccount()?.homeAccountId === account.homeAccountId) {
    return;
  }

  instance.setActiveAccount(account);
}

/**
 * Singleton MSAL PublicClientApplication instance.
 * Shared between providers.tsx (for MsalProvider) and tokenRefresh utility.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.addEventCallback((event) =>
  syncActiveAccountFromEvent(msalInstance, event)
);
